import { useQuery } from "@tanstack/react-query";
import { api } from "../api";
import { getUser } from "../utils";

const QUERY_KEY = "featureFlags";

export interface FeatureFlags {
  /** Anchored chart pills + Live Activity on the marketing dashboard. */
  enableInsights: boolean;
  /** Hero KPI insight card on the marketing dashboard. */
  enableMarketingKpiCard: boolean;
  /** Anchored chart pills on the main app dashboard. */
  enableDashboardInsights: boolean;
  /** Hero KPI narrative card on the main app dashboard. */
  enableDashboardKpiCard: boolean;
  /** Email Digest section (Settings) + digest sends. */
  enableEmailDigest: boolean;
  /** "Market trend" line + legend on the Traffic Breakdown + Trends charts. */
  enableMarketTrend: boolean;
  /**
   * Campaign Summary shows Google's five RMF reporting metrics (impressions,
   * clicks, cost, conversions, all conversions). Default false: it shows ad
   * spend, which customers do not otherwise see.
   */
  enableGoogleAdsRmfMetrics: boolean;
}

/**
 * Per-user UI feature flags, served from user_metadata via
 * GET /users/:id/feature-flags. All flags default true (visible) except
 * enableDashboardInsights (main dashboard chart pills), which defaults false
 * (hidden). Toggled per customer directly in the table.
 *
 * Note: while the query is loading, `flags` is undefined, so gating reads
 * (`flags?.enableInsights`) start hidden and reveal once data arrives - no
 * flash of gated content before the flags resolve.
 */
export function useFeatureFlags() {
  const user = getUser();
  return useQuery<FeatureFlags>({
    queryKey: [QUERY_KEY, user?.id],
    queryFn: () => api.getFeatureFlags(user!.id).then((res) => res.data),
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });
}
